// lib
import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Transition } from 'react-transition-group'

import { stopEvent } from '@aytm/lib/common/func'

import { targetChecked, isExpandableGroup } from '@aytm/app/stats/page/sidebar/data-quality/vector-check'

// components
import { Checkbox } from 'primereact/checkbox'

import DataCentrifugeTotal from '@aytm/app/stats/page/sidebar/data-quality/DataCentrifugeTotal'
import DataCentrifugeSubVector from '@aytm/app/stats/page/sidebar/data-quality/DataCentrifugeSubVector'

// styles
import style from '@aytm/app/stats/page/sidebar/data-quality/styles/DataCentrifugeSection.module.scss'

const DataCentrifugeVector = ({
  root,
  tier,
  vector,
  statuses,
  interactive,
  runEnabled = true,
  initiallyCollapsed = false,
  onCheck,
  onSelect,
  onChange,
  onFetch,
  onCollapse
}) => {
  // Below T0 the row is inert: no toggle, no solo, no Shift-range (CAPABILITY.SELECT_VECTORS).
  // The chevron stays live — expanding a group only reveals rows the tier already permits.
  const inert = vector.disabled || !interactive

  const expandable = isExpandableGroup(vector, tier)

  // For single-child flat-rendered parents, the popup's status comes from the lone
  // child rather than the parent id (the parent rarely carries its own run row).
  const flatChildId = vector.group?.length === 1 ? vector.group[0].id : null
  const effectiveStatus = statuses[vector.id] || (flatChildId && statuses[flatChildId]) || null

  // Local state mirrors the row's incoming status; useEffect below limits re-renders to
  // status changes for THIS vector instead of every list update.
  const [vectorStatus, setVectorStatus] = useState(effectiveStatus)

  // The panel unmounts on a sidebar-tab switch, so the controller keeps each row's collapse
  // for the life of the page, as it keeps the report's open sections; default expanded.
  const [collapsed, setCollapsed] = useState(initiallyCollapsed)

  const onClick = (event, vector, item) => {
    if (!onChange(event, vector, item)) {
      if (event.ctrlKey || event.metaKey) {
        onSelect(vector.id, item ? item.id : null)
      } else {
        onCheck(vector.id, item ? item.id : null, targetChecked(vector, item, tier))
      }
    }
  }

  const onToggle = useCallback(
    event => {
      stopEvent(event)

      setCollapsed(!collapsed)
      onCollapse?.(vector.id, !collapsed)
    },
    [collapsed, vector.id, onCollapse]
  )

  // Depend on the whole status object, not just its string: the popup reads message /
  // section_start / duration_sec off the same object, so a re-run landing on the same
  // status string with new details must still refresh the mirror.
  useEffect(() => setVectorStatus(effectiveStatus), [effectiveStatus])

  return (
    <>
      <div
        data-testid={`stat-data-quality-panel-dc-vector-${vector.id}`}
        // vector-active gates hover/cursor styling — disabled rows still mount so they can
        // show their status badge, but they shouldn't look clickable.
        className={classNames(`${style.row} ${style.vector}`, {
          [style['vector-active']]: !inert,
          [style['vector-collapsed']]: collapsed
        })}
        onClick={inert ? null : event => onClick(event, vector)}
      >
        <div
          data-testid={`stat-data-quality-panel-dc-vector-checkbox-${vector.id}`}
          className={`${style['vector-checkbox']} p-field`}
        >
          <Checkbox
            className="p-checkbox-sm"
            value={vector.text}
            checked={vector.disabled ? false : vector.checked}
            disabled={inert}
          />
        </div>
        <div
          data-testid={`stat-data-quality-panel-dc-vector-header-${vector.id}`}
          className={`${style['vector-header']} p-col`}
          title={Object.hasOwn(vector, 'actualTotal') ? vector.actualTotal : null}
        >
          <div className={style['vector-icon']}>
            <svg width="100" height="100" viewBox="0 0 100 100">
              <path d={vector.icon} fill={vector.disabled ? '#aeaeae' : vector.color || '#223d64'} />
            </svg>
          </div>
          <span
            data-testid={`stat-data-quality-panel-dc-vector-label-${vector.id}`}
            className={classNames(style['vector-button'], {
              [style[`vector-button-${vectorStatus?.status?.toLowerCase()}`]]: vectorStatus,
              'p-disabled':
                vector.disabled &&
                (vector.group?.every(item => item.disabled) ||
                  !vector.group?.some(item => 'success' == statuses[item.id]?.status?.toLowerCase()) ||
                  !expandable)
            })}
          >
            {vector.text}
          </span>
          {/* Toggle is exposed even for disabled groups so users can drill into a
              disabled parent and see the per-child projected counts. Only real
              expandable groups (children-list length > 1) render the chevron;
              single-child parents collapse to a flat leaf row and aggregate the
              popup at the parent id (sidebar spec §4.0.1). */}
          {expandable ? (
            <span
              data-testid={`stat-data-quality-panel-dc-vector-toggle-${vector.id}`}
              className={style['vector-toggle']}
              onClick={onToggle}
            >
              <i className="icon-aytmfd-chevron-bracket" />
            </span>
          ) : null}
        </div>
        <DataCentrifugeTotal
          root={root}
          tier={tier}
          vector={vector}
          vectorStatus={vectorStatus}
          interactive={interactive}
          runEnabled={runEnabled}
          onFetch={onFetch}
        />
      </div>
      {expandable ? (
        <Transition in={!collapsed} timeout={200}>
          {status => (
            <div
              data-testid={`stat-data-quality-panel-dc-subvectors-${vector.id}`}
              className={`${style.subvectors} ${style['transition-panel']}${
                status ? ' ' + style['transition-panel-' + status] : ''
              }`}
            >
              {vector.group.map(item => (
                <DataCentrifugeSubVector
                  key={`vector-${vector.id}-${item.id}`}
                  root={root}
                  vector={vector}
                  item={item}
                  statuses={statuses}
                  interactive={interactive}
                  runEnabled={runEnabled}
                  onClick={onClick}
                  onFetch={onFetch}
                />
              ))}
            </div>
          )}
        </Transition>
      ) : null}
    </>
  )
}

DataCentrifugeVector.propTypes = {
  root: PropTypes.any,
  tier: PropTypes.string,
  vector: PropTypes.object,
  statuses: PropTypes.object,
  interactive: PropTypes.bool,
  runEnabled: PropTypes.bool,
  initiallyCollapsed: PropTypes.bool,
  onCheck: PropTypes.func,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  onFetch: PropTypes.func,
  onCollapse: PropTypes.func
}

DataCentrifugeVector.displayName = 'DataCentrifugeVector'

export default DataCentrifugeVector
